import React, { useState, useRef, useEffect } from "react";
import { useDeal } from "../lib/store";
import { streamPost, chatApi } from "../lib/api";
import { toast } from "sonner";
import { Globe, Cpu } from "lucide-react";

// Render assistant markdown: strip [n] citation markers, bold **x**, bullet/numbered lists, headings, paragraphs.
const renderRich = (text) => {
  const clean = (text || "").replace(/\s*\[[0-9]+(?:\s*,\s*[0-9]+)*\]/g, "");
  const inline = (s, kp) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return parts.map((p, j) => p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${kp}-${j}`}>{p.slice(2, -2)}</strong>
      : <React.Fragment key={`${kp}-${j}`}>{p}</React.Fragment>);
  };
  const lines = clean.split("\n").map((l) => l.trim());
  const out = [];
  let list = null; // {type:'ul'|'ol', items:[]}
  let para = [];
  const flushPara = () => { if (para.length) { const p = para; out.push(<p key={`p${out.length}`} style={{ margin: "0 0 8px" }}>{p.map((l, i) => <React.Fragment key={i}>{inline(l, `p${out.length}-${i}`)}{i < p.length - 1 && <br />}</React.Fragment>)}</p>); para = []; } };
  const flushList = () => { if (list) { const L = list; const Tag = L.type; out.push(<Tag key={`l${out.length}`} style={{ margin: "6px 0", paddingLeft: 20 }}>{L.items.map((it, i) => <li key={i} style={{ margin: "2px 0" }}>{inline(it, `l${out.length}-${i}`)}</li>)}</Tag>); list = null; } };
  lines.forEach((l) => {
    if (!l) { flushPara(); flushList(); return; }
    if (/^\|?\s*-{3,}[\s|:-]*$/.test(l) || /^\|[\s:-]+\|$/.test(l)) return;   // markdown rules / table dividers
    if (l.startsWith("|") && l.endsWith("|")) {                               // stray table row -> one readable line
      const cells = l.split("|").map((c) => c.trim()).filter(Boolean);
      if (cells.length) { flushList(); para.push(cells.join(" — ")); }
      return;
    }
    const bullet = l.match(/^[-•*]\s+(.*)/);
    const numbered = l.match(/^\d+[.)]\s+(.*)/);
    const heading = l.match(/^#{1,4}\s+(.*)/);
    if (heading) { flushPara(); flushList(); out.push(<p key={`h${out.length}`} style={{ margin: "8px 0 4px", fontWeight: 700 }}>{inline(heading[1], `h${out.length}`)}</p>); return; }
    if (bullet) { flushPara(); if (!list || list.type !== "ul") { flushList(); list = { type: "ul", items: [] }; } list.items.push(bullet[1]); return; }
    if (numbered) { flushPara(); if (!list || list.type !== "ol") { flushList(); list = { type: "ol", items: [] }; } list.items.push(numbered[1]); return; }
    flushList(); para.push(l);
  });
  flushPara(); flushList();
  return out;
};

const STARTERS = [
  "What is the DSCR if I pay myself $80,000?",
  "How does the SLB affect my entry multiple?",
  "What are current SBA 7(a) rates and typical terms right now?",
  "What EBITDA multiples are comparable landscaping businesses trading at?",
  "What are the biggest red flags in this deal?",
  "What seller note terms should I negotiate?",
];

export default function AIAssistant() {
  const { M } = useDeal();
  const [messages, setMessages] = useState([
    { role: "assistant", content: `Hi — I'm your XYZ Ventures deal analyst. I have the full model for ${M.dealName} loaded, and I can search the live web for market data, comps and rates. Ask me anything.` },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [web, setWeb] = useState(true);
  const logRef = useRef(null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      if (web) {
        try {
          const res = await chatApi.web({ messages: next.filter((m) => m.role !== "system"), deal: M });
          setLoading(false);
          setMessages([...next, { role: "assistant", content: res.answer, citations: res.citations || [] }]);
        } catch (webErr) {
          // Live web search is down (usually the Perplexity quota) — fall back to the built-in model
          // so the question still gets answered, and say why the web sources are missing.
          const why = webErr?.response?.data?.detail || "Live web search is unavailable right now.";
          toast.error(why);
          let started = false;
          await streamPost("/ai/chat", { messages: next.filter((m) => m.role !== "system"), deal: M }, (chunk, full) => {
            if (!started) { started = true; setLoading(false); }
            setMessages([...next, { role: "assistant", content: `_${why} Answering from the deal model and my own knowledge — no live sources._\n\n${full}` }]);
          });
        }
      } else {
        let started = false;
        await streamPost("/ai/chat", { messages: next.filter((m) => m.role !== "system"), deal: M }, (chunk, full) => {
          if (!started) { started = true; setLoading(false); }
          setMessages([...next, { role: "assistant", content: full }]);
        });
      }
    } catch (e) {
      setMessages([...next, { role: "assistant", content: "⚠ Connection failed — please try again." }]);
      toast.error("Connection failed — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page" data-testid="page-assistant">
      <div className="page-head">
        <div className="page-title">AI Assistant</div>
        <div className="page-sub">{web ? "Web-grounded analyst (Perplexity) · live sources + deal context" : "Fast analyst (Claude) · deal context only"}</div>
      </div>

      <div className="chat-wrap">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <div className="web-mode" data-testid="web-toggle-bar">
            <button className={`btn ${web ? "btn-primary" : "btn-ghost"}`} onClick={() => setWeb(true)} data-testid="web-toggle-on"
              title="Search the live web for market data, comps and rates">
              <Globe size={13} />Web search
            </button>
            <button className={`btn ${!web ? "btn-primary" : "btn-ghost"}`} onClick={() => setWeb(false)} data-testid="web-toggle"
              title="Answer from the deal model and the built-in model only">
              <Cpu size={13} />Model only
            </button>
          </div>
        </div>
        <div className="chat-log" ref={logRef} data-testid="chat-log">
          {messages.map((m, i) => (
            <div key={i} className={`msg ${m.role === "user" ? "user" : "bot"}`} data-testid={`msg-${m.role}`}>
              {m.role === "assistant" ? renderRich(m.content) : m.content}
              {m.citations && m.citations.length > 0 && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text3)", marginBottom: 4 }}>Sources</div>
                  {m.citations.map((c, j) => (
                    <a key={j} href={c.url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 11, color: "var(--text2)" }} data-testid={`chat-src-${j}`}>{j + 1}. {c.title}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
          {loading && <div className="msg bot"><span className="typing"><span /><span /><span /></span></div>}
        </div>

        {messages.length <= 1 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "10px 0" }}>
            {STARTERS.map((s) => <button key={s} className="chip" onClick={() => send(s)} data-testid="starter-chip">{s}</button>)}
          </div>
        )}

        <div className="chat-input">
          <input className="inp" value={input} placeholder="Ask about DSCR, structure, valuation, live comps…" onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="chat-input" />
          <button className="btn btn-primary" onClick={() => send()} disabled={loading} data-testid="chat-send">Send</button>
        </div>
      </div>
    </div>
  );
}
